import React, { useState } from 'react';

const API_GUIDE_CONTENT = {
  quickStart: {
    title: '🚀 Quick Start',
    content: `
1. Get your JWT token: POST /api/auth/login
2. Save the token securely
3. Use it in Authorization header for all requests

Example:
\`\`\`bash
curl -X POST http://localhost:8080/api/auth/login \\
  -H "Content-Type: application/json" \\
  -d '{"username":"admin","password":"password"}'

# Response: { "token": "eyJhbGc...", "user": {...} }

# Use token in subsequent requests:
curl http://localhost:8080/api/radios \\
  -H "Authorization: Bearer eyJhbGc..." \\
  -H "X-Org-ID: 550e8400-e29b-41d4-a716-446655440000"
\`\`\`

**Token expires after 24 hours. Re-authenticate to get a new token.**
    `
  },
  endpoints: {
    title: '📍 All 69 Endpoints',
    content: `
**System & Health (3)**
- GET /health - Health check
- GET /api/config - Get system config
- PUT /api/config - Update system config

**Authentication (2)**
- POST /api/auth/login - Login (returns JWT token)
- POST /api/auth/register - Register new user

**Organizations (5)**
- GET /api/admin/organizations - List organizations
- POST /api/admin/organizations - Create organization
- GET /api/admin/organizations/{id} - Get organization
- PUT /api/admin/organizations/{id} - Rename network ({"name"})
- GET /api/admin/organizations/{id}/delete-preview - Network delete impact (platform admin)
- DELETE /api/admin/organizations/{id} - Delete network (platform admin, MFA session; {"confirm_name", "preview_fingerprint"})

**Users (7)**
- GET /api/admin/users - List all users
- POST /api/admin/users - Create new user
- GET /api/admin/users/admins - List admins only
- GET /api/admin/users/{id} - Get user details
- PUT /api/admin/users/{id} - Update user
- DELETE /api/admin/users/{id} - Delete user
- PUT /api/admin/users/{id}/role - Change user role

**Services (5)**
- GET /api/admin/services - Get all services status
- GET /api/admin/services/{id}/status - Get service status
- POST /api/admin/services/{id}/start - Start service
- POST /api/admin/services/{id}/stop - Stop service
- POST /api/admin/services/{id}/restart - Restart service

**API Keys (5)**
- GET /api/settings/api-keys - List API keys
- POST /api/settings/api-keys - Create API key
- GET /api/settings/api-keys/{id} - Get API key
- DELETE /api/settings/api-keys/{id} - Delete API key
- POST /api/settings/api-keys/{id}/revoke - Revoke API key

**Radios (5)**
- POST /api/radios - Create radio
- GET /api/radios - List radios (supports limit/offset)
- GET /api/radios/{id} - Get radio details
- PUT /api/radios/{id} - Update radio
- DELETE /api/radios/{id} - Delete radio

**Provisioning (3)**
- POST /api/provisioning/tokens - Generate tokens
- GET /api/provisioning/tokens - List tokens
- POST /api/radios/enroll - Enroll radio with token

**Groups (5)**
- POST /api/groups - Create group
- GET /api/groups - List groups
- GET /api/groups/{id} - Get group
- PUT /api/groups/{id} - Update group
- DELETE /api/groups/{id} - Delete group

**Group Members (3)**
- POST /api/groups/{id}/members - Add member
- DELETE /api/groups/{id}/members/{radioId} - Remove member
- GET /api/groups/{id}/members - List members

**Presence (3)**
- POST /api/presence - Update presence
- GET /api/presence/{radioId} - Get radio presence
- GET /api/presence - Get all presence

**Floor Control (5)**
- POST /api/floor/request - Request floor control
- POST /api/floor/release - Release floor control
- GET /api/floor/{groupId} - Get floor status
- GET /api/floor/{groupId}/history - Get history
- POST /api/floor/{groupId}/token/refresh - Refresh token

**Location (4)**
- GET /api/location/radios - Get all locations
- GET /api/location/radios/{radioId}/history - Get location history
- GET /api/location/nearby - Find radios nearby

**INRICO Integration (10)**
- POST /api/inrico/register - Register INRICO radio
- PUT /api/inrico/{id}/heartbeat - Send heartbeat
- GET /api/inrico/{id}/status - Get radio status
- POST /api/inrico/{id}/ppt/request - Request PTT
- POST /api/inrico/{id}/ppt/release - Release PTT
- POST /api/inrico/{id}/sos - Trigger SOS event
- GET /api/inrico/sos/events - Get SOS events
- GET /api/inrico/{id}/config - Get radio config
- GET /api/inrico/fleet/status - Get fleet status
- GET /api/inrico/health - Health check

**WebSocket (2)**
- GET /api/ws - WebSocket connection
- GET /ws - WebSocket (alternative)

**Synchronization (2)**
- POST /api/admin/organizations/sync/booking - Sync orgs
- GET /api/admin/organizations/sync/status - Sync status
    `
  },
  security: {
    title: '🔐 Security & Headers',
    content: `
**Required Headers:**
- Authorization: Bearer <JWT_TOKEN>
  → JWT token with 24-hour expiration
  → Obtained from POST /api/auth/login
  
- X-Org-ID: <ORG_UUID>
  → Multi-tenant organization isolation
  → Required for most endpoints
  → Default: 550e8400-e29b-41d4-a716-446655440000

- X-Radio-ID: <RADIO_UUID> (optional)
  → For radio-specific context
  → Used in floor control and presence
  
- Content-Type: application/json
  → For POST/PUT requests

**Security Best Practices:**
✅ Always use HTTPS in production
✅ Store tokens in secure storage (HttpOnly cookies preferred)
✅ Never share tokens or log them
✅ Rotate tokens regularly (24h expiration)
✅ Use organization scoping for multi-tenant isolation
✅ Implement rate limiting on client side
✅ Monitor API usage and access logs
✅ Revoke API keys immediately if compromised

**Authentication Flow:**
1. POST /api/auth/login with username/password
2. Receive JWT token (valid for 24 hours)
3. Include token in Authorization header
4. Token expires → re-authenticate to get new token

**Organization Isolation:**
All data is organization-scoped via X-Org-ID header.
Each organization has separate:
- Users and admins
- Radios and groups
- Floor control and presence data
- Location history
- API keys
    `
  },
  bestPractices: {
    title: '💡 Best Practices',
    content: `
**Endpoint Usage:**
1. Always include X-Org-ID header
2. Check response status codes
3. Implement exponential backoff for retries
4. Cache responses when appropriate
5. Use pagination (limit/offset) for large results

**Pagination:**
?limit=50       → Max items to return
?offset=100     → Skip first N items

Example:
GET /api/radios?limit=50&offset=100

**Common Status Codes:**
- 200: Success (data returned)
- 201: Created (resource created)
- 204: No Content (success, no body)
- 400: Bad Request (invalid parameters)
- 401: Unauthorized (missing/invalid token)
- 403: Forbidden (no permission)
- 404: Not Found (resource doesn't exist)
- 409: Conflict (duplicate/conflict)
- 500: Server Error (backend issue)

**Error Handling:**
\`\`\`json
{
  "error": "Error message",
  "details": "Additional context",
  "status": "error_code"
}
\`\`\`

**Performance Tips:**
1. Use WebSocket for real-time updates
2. Batch operations when possible
3. Implement connection pooling
4. Cache authentication tokens
5. Use appropriate limit/offset for pagination
6. Monitor API response times

**Workflow Examples:**

Create and List Radios:
1. POST /api/radios - Create radio
2. GET /api/radios?limit=50 - List radios

Create Group and Add Radios:
1. POST /api/groups - Create group
2. POST /api/groups/{id}/members - Add radio to group
3. GET /api/groups/{id}/members - Verify members

Request Floor and Transmit:
1. POST /api/floor/request - Request floor
2. App transmits on granted floor
3. POST /api/floor/release - Release floor
    `
  },
  examples: {
    title: '💻 Code Examples',
    content: `
**Base URL:** http://localhost:8080/api

**Python (requests):**
\`\`\`python
import requests

BASE_URL = "http://localhost:8080/api"
token = "eyJhbGc..."  # From login endpoint
org_id = "550e8400-e29b-41d4-a716-446655440000"

headers = {
    "Authorization": f"Bearer {token}",
    "X-Org-ID": org_id,
    "Content-Type": "application/json"
}

# GET request
response = requests.get(f"{BASE_URL}/radios", headers=headers)
radios = response.json()

# POST request
data = {"radio_id": "UNIT-001", "model": "S380", "name": "Unit 1"}
response = requests.post(f"{BASE_URL}/radios", headers=headers, json=data)
new_radio = response.json()

# PUT request
response = requests.put(
    f"{BASE_URL}/radios/{radio_id}",
    headers=headers,
    json={"name": "Updated Unit 1"}
)

# DELETE request
response = requests.delete(f"{BASE_URL}/radios/{radio_id}", headers=headers)
\`\`\`

**JavaScript (Fetch):**
\`\`\`javascript
const BASE_URL = "http://localhost:8080/api";
const token = "eyJhbGc...";  // From login
const orgId = "550e8400-e29b-41d4-a716-446655440000";

const headers = {
  "Authorization": "Bearer " + token,
  "X-Org-ID": orgId,
  "Content-Type": "application/json"
};

// GET request
const response = await fetch(BASE_URL + "/radios", { headers });
const radios = await response.json();

// POST request
const createResponse = await fetch(BASE_URL + "/radios", {
  method: "POST",
  headers,
  body: JSON.stringify({ 
    radio_id: "UNIT-001", 
    model: "S380", 
    name: "Unit 1" 
  })
});
const newRadio = await createResponse.json();

// PUT request
await fetch(BASE_URL + "/radios/" + radioId, {
  method: "PUT",
  headers,
  body: JSON.stringify({ name: "Updated Unit 1" })
});

// DELETE request
await fetch(BASE_URL + "/radios/" + radioId, {
  method: "DELETE",
  headers
});
\`\`\`

**JavaScript (Axios):**
\`\`\`javascript
import axios from 'axios';

const api = axios.create({
  baseURL: "http://localhost:8080/api",
  headers: {
    "Authorization": "Bearer " + token,
    "X-Org-ID": orgId
  }
});

// GET
const radios = await api.get('/radios');

// POST
const newRadio = await api.post('/radios', {
  radio_id: "UNIT-001",
  model: "S380",
  name: "Unit 1"
});

// PUT
await api.put('/radios/' + radioId, {
  name: "Updated Unit 1"
});

// DELETE
await api.delete('/radios/' + radioId);
\`\`\`

**cURL:**
\`\`\`bash
TOKEN="eyJhbGc..."
ORG_ID="550e8400-e29b-41d4-a716-446655440000"
BASE_URL="http://localhost:8080/api"

# Login
curl -X POST $BASE_URL/auth/login \\
  -H "Content-Type: application/json" \\
  -d '{"username":"admin","password":"password"}'

# GET
curl $BASE_URL/radios \\
  -H "Authorization: Bearer $TOKEN" \\
  -H "X-Org-ID: $ORG_ID"

# POST
curl -X POST $BASE_URL/radios \\
  -H "Authorization: Bearer $TOKEN" \\
  -H "X-Org-ID: $ORG_ID" \\
  -H "Content-Type: application/json" \\
  -d '{"radio_id":"UNIT-001","model":"S380","name":"Unit 1"}'

# PUT
curl -X PUT $BASE_URL/radios/$RADIO_ID \\
  -H "Authorization: Bearer $TOKEN" \\
  -H "X-Org-ID: $ORG_ID" \\
  -H "Content-Type: application/json" \\
  -d '{"name":"Updated Unit 1"}'

# DELETE
curl -X DELETE $BASE_URL/radios/$RADIO_ID \\
  -H "Authorization: Bearer $TOKEN" \\
  -H "X-Org-ID: $ORG_ID"
\`\`\`
    `
  },
  errorHandling: {
    title: '⚠️ Error Handling',
    content: `
**Status Codes Reference:**
- 200 OK: Request successful (data returned)
- 201 Created: Resource created successfully
- 204 No Content: Success (no response body)
- 400 Bad Request: Invalid parameters or malformed request
- 401 Unauthorized: Missing or invalid authentication token
- 403 Forbidden: Authenticated but not authorized
- 404 Not Found: Resource doesn't exist
- 409 Conflict: Duplicate resource or state conflict
- 500 Server Error: Internal server error
- 503 Unavailable: Service temporarily unavailable

**Common Error Scenarios:**

**401 Unauthorized**
Cause: Missing or expired token
Solution:
  → Check Authorization header is present
  → Verify token format: "Authorization: Bearer <token>"
  → Token expires after 24 hours
  → Re-authenticate via POST /api/auth/login

**404 Not Found**
Cause: Resource doesn't exist
Solution:
  → Verify resource ID is correct
  → Check resource wasn't deleted
  → Verify organization ID matches

**409 Conflict**
Cause: Duplicate resource or invalid state
Solution:
  → Radio may already exist
  → Member already in group
  → Use different name/identifier

**400 Bad Request**
Cause: Invalid request format
Solution:
  → Check request body JSON is valid
  → Verify all required fields present
  → Match field types (string, number, boolean)
  → Check field values are valid

**500 Server Error**
Cause: Backend issue
Solution:
  → Check server logs
  → Verify database connection
  → Wait and retry
  → Contact support if persists

**Error Response Format:**
\`\`\`json
{
  "error": "Error message",
  "details": "Additional context",
  "status": "error_code"
}
\`\`\`
    `
  },
  faq: {
    title: '❓ FAQ',
    content: `
**Authentication**
Q: How do I authenticate?
A: POST /api/auth/login with username/password to get JWT token.
   Include token in all requests: Authorization: Bearer <token>

Q: How long is a token valid?
A: 24 hours. Re-authenticate to get a new token.

Q: What if my token expires?
A: You'll get 401 Unauthorized. Login again to get new token.

**Endpoints**
Q: How many endpoints are there?
A: 69 endpoints across 17 categories:
   System (3), Auth (2), Organizations (5), Users (7), Services (5),
   API Keys (5), Radios (5), Provisioning (3), Groups (5),
   Group Members (3), Presence (3), Floor Control (5), Location (4),
   INRICO (10), WebSocket (2), Sync (2)

Q: What's the base URL?
A: http://localhost:8080/api (path varies per endpoint)

Q: Do I need X-Org-ID header?
A: Yes, for most endpoints. It determines which organization's data.

**Data Management**
Q: Can I delete a radio after using it?
A: Yes, use DELETE /api/radios/{id}
   Deletes radio and associated data.

Q: How do I get radio location history?
A: GET /api/location/radios/{radioId}/history

Q: Can I list radios with pagination?
A: Yes, use ?limit=50&offset=100 query parameters

**Real-time Updates**
Q: How do I get real-time updates?
A: Connect via WebSocket: GET /api/ws
   Receives floor control and presence updates.

Q: Can I update presence via API?
A: Yes, POST /api/presence with status (available/busy/offline/dnd)

**Multi-tenant**
Q: How is data isolated between organizations?
A: X-Org-ID header isolates all data by organization.
   Each org has separate radios, groups, users, etc.

Q: Can I access data from other organizations?
A: No, X-Org-ID header enforces strict isolation.

**Performance**
Q: How should I handle large result sets?
A: Use pagination: ?limit=50&offset=0
   Fetch pages as needed.

Q: Should I poll for updates?
A: No, use WebSocket for real-time updates instead.
   Much more efficient.

**Floor Control**
Q: How does floor control work?
A: 1. POST /api/floor/request - Request permission
   2. Response: granted/denied with token
   3. Transmit on floor (your app handles)
   4. POST /api/floor/release - Release when done

Q: What if floor is already in use?
A: Request is denied. Try again when it's available.

**See Full Documentation:**
For complete endpoint specifications, request/response examples,
and detailed API documentation, see the full API reference guide.
    `
  }
};

export default function ApiGuide() {
  const [activeSection, setActiveSection] = useState('quickStart');

  const sections = Object.keys(API_GUIDE_CONTENT) as Array<keyof typeof API_GUIDE_CONTENT>;

  return (
    <div className="space-y-6">
      {/* Interactive Swagger UI Link */}
      <div className="bg-gradient-to-r from-green-900/40 to-blue-900/40 border border-green-600 rounded-lg p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-green-300 mb-1">🧪 Interactive API Explorer (Swagger UI)</h3>
          <p className="text-sm text-gray-300">
            Browse and test all 69 endpoints live, with request/response schemas and a built-in
            "Try it out" console.
          </p>
        </div>
        <a
          href="http://localhost:8080/api/docs"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center px-5 py-3 rounded bg-green-600 hover:bg-green-500 text-white text-sm font-semibold transition whitespace-nowrap"
        >
          Open Swagger UI ↗
        </a>
      </div>

      {/* Guide Navigation */}
      <div className="bg-gray-800 rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4 text-gray-200">API Guide Sections</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {sections.map((section) => (
            <button
              key={section}
              onClick={() => setActiveSection(section)}
              className={`px-3 py-2 rounded text-sm font-medium transition ${
                activeSection === section
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              {API_GUIDE_CONTENT[section].title.split(' ')[0]}
              {' '}
              {API_GUIDE_CONTENT[section].title.split(' ').slice(1).join(' ')}
            </button>
          ))}
        </div>
      </div>

      {/* Content Display */}
      <div className="bg-gray-800 rounded-lg p-6">
        <h2 className="text-2xl font-bold mb-4 text-white">
          {API_GUIDE_CONTENT[activeSection].title}
        </h2>
        <div className="prose prose-invert max-w-none">
          <pre className="bg-gray-900 rounded p-4 overflow-x-auto text-sm text-gray-300 whitespace-pre-wrap break-words">
            {API_GUIDE_CONTENT[activeSection].content.trim()}
          </pre>
        </div>
      </div>

      {/* Key Information Card */}
      <div className="bg-blue-900/30 border border-blue-600 rounded-lg p-4">
        <h3 className="text-blue-400 font-semibold mb-2">ℹ️ API Overview</h3>
        <ul className="text-sm text-blue-300 space-y-1">
          <li>✓ <strong>69 Total Endpoints</strong> across 17 categories</li>
          <li>✓ <strong>RESTful API</strong> with standard HTTP methods</li>
          <li>✓ <strong>JWT Authentication</strong> with 24-hour expiration</li>
          <li>✓ <strong>Multi-tenant</strong> organization isolation</li>
          <li>✓ <strong>WebSocket</strong> for real-time updates</li>
          <li>✓ <strong>Complete Documentation</strong> available</li>
        </ul>
      </div>

      {/* Quick Reference */}
      <div className="bg-gray-800 rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4 text-gray-200">Quick Reference</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-700 rounded p-3">
            <p className="text-sm text-gray-400 mb-2">Base URL</p>
            <code className="text-xs text-gray-200 break-all">
              http://localhost:8080/api
            </code>
          </div>
          <div className="bg-gray-700 rounded p-3">
            <p className="text-sm text-gray-400 mb-2">Default Org ID</p>
            <code className="text-xs text-gray-200 break-all">
              550e8400-e29b-41d4-a716-446655440000
            </code>
          </div>
          <div className="bg-gray-700 rounded p-3">
            <p className="text-sm text-gray-400 mb-2">Required Header</p>
            <code className="text-xs text-gray-200">
              X-Org-ID: &lt;org-uuid&gt;
            </code>
          </div>
          <div className="bg-gray-700 rounded p-3">
            <p className="text-sm text-gray-400 mb-2">Auth Header</p>
            <code className="text-xs text-gray-200">
              Authorization: Bearer &lt;jwt-token&gt;
            </code>
          </div>
        </div>
      </div>

      {/* Endpoints Summary */}
      <div className="bg-gray-800 rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4 text-gray-200">Endpoint Categories (69 Total)</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs text-gray-300">
          <div>📊 System (3)</div>
          <div>🔐 Auth (2)</div>
          <div>🏢 Organizations (5)</div>
          <div>👥 Users (7)</div>
          <div>⚙️ Services (5)</div>
          <div>🔑 API Keys (5)</div>
          <div>📻 Radios (5)</div>
          <div>📋 Provisioning (3)</div>
          <div>👨‍👩‍👧‍👦 Groups (5)</div>
          <div>➕ Members (3)</div>
          <div>💚 Presence (3)</div>
          <div>🎙️ Floor Control (5)</div>
          <div>📍 Location (4)</div>
          <div>📡 INRICO (10)</div>
          <div>🔌 WebSocket (2)</div>
          <div>🔄 Sync (2)</div>
        </div>
      </div>
    </div>
  );
}

